import type { AIJobStatusPayload } from '@/types/ai';
import {
  AI_GENERATION_FAILURE_FALLBACK_MESSAGE,
  resolveAiGenerationFailureMessage,
} from '@/lib/ai-generation-failure-toast';
import { resolveGenerationPollIntervalMs } from '@/lib/preview-generation';
import type { GenerationTask } from '@/stores/generationStore';

export type CanvasGenerationStatus = 'pending' | 'processing' | 'completed' | 'failed' | null;

export const CANVAS_GENERATION_SYNC_WARNING_TITLE = '任务已创建';
export const CANVAS_GENERATION_SYNC_WARNING_DESCRIPTION =
  '画布状态同步失败，请刷新页面查看最新状态';

export type CanvasGenerationPostSubmitSyncResult =
  | { synchronized: true }
  | { synchronized: false; error: unknown };

export interface CanvasGenerationNodeStatus {
  generationJobId?: string | null;
  generationStatus?: CanvasGenerationStatus;
  generationProgress?: number | null;
  /** 后端预估剩余秒数；瞬时值，不随 layout 持久化，刷新后由轮询重新给 */
  generationEstimatedRemainingSeconds?: number | null;
  generationStartedByUserId?: string | null;
  generationConversationId?: string | null;
  generationMessageId?: string | null;
  generationJobCreatedAt?: string | null;
  /** 厂商状态；瞬时值，来自已有 batch 轮询，不随 layout 持久化 */
  generationProviderStatus?: string | null;
  generationOverlayLabel?: string | null;
  generationJobIds?: string[] | null;
}

/** 画板进行中的会话生成 job；conversation* 供标题栏撤回，缺省时按钮不出现。 */
export interface CanvasGenerationJobRecord {
  jobId: string;
  jobIds?: string[];
  conversationId?: string | null;
  messageId?: string | null;
  jobCreatedAt?: string | null;
}

/**
 * AI job 已创建后的画布状态同步属于非阻断阶段：同步失败不得反向判定生成提交失败。
 */
export async function runCanvasGenerationPostSubmitSync(
  syncCanvasState: () => void | Promise<void>
): Promise<CanvasGenerationPostSubmitSyncResult> {
  try {
    await syncCanvasState();
    return { synchronized: true };
  } catch (error) {
    return { synchronized: false, error };
  }
}

/** 原子认领一次失败 job，避免 React effect 重跑时重复执行任务收口。 */
export function claimCanvasGenerationFailureJob(
  processedJobIds: Set<string>,
  jobId: string
): boolean {
  if (processedJobIds.has(jobId)) return false;
  processedJobIds.add(jobId);
  return true;
}

export function isActiveCanvasGenerationTaskStatus(status: GenerationTask['status']): boolean {
  return status === 'processing' || status === 'finishing';
}

type StoryboardVideoUpscaleGenerationTask = Pick<
  GenerationTask,
  | 'localTaskId'
  | 'jobId'
  | 'storyboardId'
  | 'previewItemId'
  | 'mode'
  | 'assetType'
  | 'prompt'
  | 'status'
  | 'progress'
>;

export function buildStoryboardVideoUpscaleGenerationByVideoId(
  tasks: readonly StoryboardVideoUpscaleGenerationTask[],
  storyboardId: string | null | undefined,
  jobStatuses: Record<string, AIJobStatusPayload> = {}
): Record<string, CanvasGenerationNodeStatus> {
  if (!storyboardId) return {};
  const generationByVideoId: Record<string, CanvasGenerationNodeStatus> = {};

  for (const task of tasks) {
    if (
      task.storyboardId !== storyboardId ||
      task.mode !== 'storyboard-video' ||
      task.assetType !== 'video' ||
      task.prompt !== 'video_upscale' ||
      !task.previewItemId ||
      generationByVideoId[task.previewItemId]
    ) {
      continue;
    }
    const payload = task.jobId ? jobStatuses[task.jobId] : undefined;
    const remoteStatus = normalizeCanvasGenerationStatus(payload?.status);
    const generationStatus =
      remoteStatus ??
      (task.status === 'processing' || task.status === 'finishing' ? 'processing' : null);
    if (generationStatus !== 'pending' && generationStatus !== 'processing') continue;
    generationByVideoId[task.previewItemId] = {
      generationJobId: task.jobId ?? null,
      generationStatus,
      generationProgress: Math.max(
        task.progress,
        getCanvasGenerationProgress(payload) ?? task.progress
      ),
      generationEstimatedRemainingSeconds: getCanvasGenerationEstimatedRemainingSeconds(payload),
    };
  }

  return generationByVideoId;
}

/**
 * 「已生成视频节点上再生成」的就地 loading（D 方案）。
 *
 * 与升频区分：
 * - 升频（`buildStoryboardVideoUpscaleGenerationByVideoId`）完成后迁移/替换源节点；
 * - 本函数覆盖普通再生成（`prompt !== 'video_upscale'`），完成后新增独立节点，源节点保留。
 *
 * 通过 `previewItemId ∈ existingVideoIds` 精准排除临时节点任务（其 previewItemId 是
 * draftId，不在现有 videoId 集合内）。
 */
export function buildStoryboardVideoGenerationByVideoId(
  tasks: readonly StoryboardVideoUpscaleGenerationTask[],
  storyboardId: string | null | undefined,
  existingVideoIds: ReadonlySet<string>,
  jobStatuses: Record<string, AIJobStatusPayload> = {}
): Record<string, CanvasGenerationNodeStatus> {
  if (!storyboardId) return {};
  const generationByVideoId: Record<string, CanvasGenerationNodeStatus> = {};

  for (const task of tasks) {
    if (
      task.storyboardId !== storyboardId ||
      task.mode !== 'storyboard-video' ||
      task.assetType !== 'video' ||
      task.prompt === 'video_upscale' ||
      !task.previewItemId ||
      !existingVideoIds.has(task.previewItemId) ||
      generationByVideoId[task.previewItemId]
    ) {
      continue;
    }
    const payload = task.jobId ? jobStatuses[task.jobId] : undefined;
    const remoteStatus = normalizeCanvasGenerationStatus(payload?.status);
    const generationStatus =
      remoteStatus ??
      (task.status === 'processing' || task.status === 'finishing' ? 'processing' : null);
    if (generationStatus !== 'pending' && generationStatus !== 'processing') continue;
    generationByVideoId[task.previewItemId] = {
      generationJobId: task.jobId ?? null,
      generationStatus,
      generationProgress: Math.max(
        task.progress,
        getCanvasGenerationProgress(payload) ?? task.progress
      ),
      generationEstimatedRemainingSeconds: getCanvasGenerationEstimatedRemainingSeconds(payload),
    };
  }

  return generationByVideoId;
}

type RestoredGenerationTask = Pick<
  GenerationTask,
  'jobId' | 'mode' | 'assetType' | 'tagId' | 'storyboardId' | 'resourceId' | 'status' | 'origin'
>;

const ACTIVE_RESTORED_TASK_STATUSES: ReadonlySet<GenerationTask['status']> = new Set([
  'processing',
  'finishing',
]);

export function collectCanvasGenerationJobIds(
  job: Pick<CanvasGenerationJobRecord, 'jobId' | 'jobIds'>
): string[] {
  const ids = job.jobIds?.filter((id) => typeof id === 'string' && id.trim().length > 0);
  if (ids && ids.length > 0) return Array.from(new Set(ids));
  return job.jobId ? [job.jobId] : [];
}

const CANVAS_GENERATION_TERMINAL_STATUSES = new Set(['completed', 'failed']);

export function areCanvasGenerationJobsTerminal(
  jobIds: readonly string[],
  statuses: Record<string, Pick<AIJobStatusPayload, 'status'> | undefined>
): boolean {
  if (jobIds.length === 0) return false;
  return jobIds.every((jobId) => {
    const normalized = normalizeCanvasGenerationStatus(statuses[jobId]?.status);
    return normalized != null && CANVAS_GENERATION_TERMINAL_STATUSES.has(normalized);
  });
}

/**
 * overlay 展示态：sibling 未全部终态时，不得把主 job 的 completed/failed 写进节点。
 * 否则 isGenerating 会提前关掉遮罩，layout 还可能把 completed 落库，刷新后丢 sibling。
 */
export function resolveCanvasGenerationOverlayStatus(
  jobIds: readonly string[],
  statuses: Record<string, Pick<AIJobStatusPayload, 'status'> | undefined>,
  composedStatus: CanvasGenerationStatus
): CanvasGenerationStatus {
  if (areCanvasGenerationJobsTerminal(jobIds, statuses)) {
    return composedStatus;
  }
  return composedStatus === 'pending' ? 'pending' : 'processing';
}

export function averageCanvasGenerationProgress(
  payloads: Array<AIJobStatusPayload | undefined>
): number | null {
  const values = payloads
    .map((payload) => getCanvasGenerationProgress(payload))
    .filter((value): value is number => typeof value === 'number');
  if (values.length === 0) return null;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function unionCanvasGenerationJobIds(
  ...jobs: Array<Pick<CanvasGenerationJobRecord, 'jobId' | 'jobIds'> | undefined>
): string[] {
  const ids: string[] = [];
  for (const job of jobs) {
    if (!job) continue;
    ids.push(...collectCanvasGenerationJobIds(job));
  }
  return Array.from(new Set(ids));
}

/**
 * 刷新恢复：把 generationStore 中进行中的分镜图片任务桥接为「planId → jobId」。
 * 关键：分镜图片会话的 `tag_id` 即 planId（后端 /ai/jobs source 直接返回），
 * 故 restored task.tagId 就是画布 loading 归属的 planId。
 * image-split 的 split-pending 占位只属于监视器，不映射到画板节点。
 */
export function buildStoryboardImageGenerationJobsFromTasks(
  tasks: readonly RestoredGenerationTask[],
  storyboardId: string | null | undefined
): Record<string, CanvasGenerationJobRecord> {
  if (!storyboardId) return {};
  const jobs: Record<string, CanvasGenerationJobRecord> = {};
  for (const task of tasks) {
    if (
      task.mode !== 'storyboard-image' ||
      task.assetType !== 'image' ||
      task.storyboardId !== storyboardId ||
      !task.tagId ||
      !task.jobId ||
      !ACTIVE_RESTORED_TASK_STATUSES.has(task.status)
    ) {
      continue;
    }
    if (task.origin === 'image-split' && task.tagId.startsWith('split-pending:')) {
      continue;
    }
    const existing = jobs[task.tagId];
    if (!existing) {
      jobs[task.tagId] = { jobId: task.jobId, jobIds: [task.jobId] };
      continue;
    }
    const jobIds = unionCanvasGenerationJobIds(existing, { jobId: task.jobId });
    jobs[task.tagId] = {
      ...existing,
      jobIds,
    };
  }
  return jobs;
}

/**
 * 刷新恢复：把 generationStore 中进行中的资产标签任务桥接为「tagId → jobId」。
 * 资产标签会话的 `tag_id` 即 element_tag_id；按 resourceId 限定当前资产画布范围。
 */
export function buildTagGenerationJobsFromTasks(
  tasks: readonly RestoredGenerationTask[],
  resourceId: string | null | undefined
): Record<string, CanvasGenerationJobRecord> {
  if (!resourceId) return {};
  const jobs: Record<string, CanvasGenerationJobRecord> = {};
  for (const task of tasks) {
    if (
      task.assetType !== 'image' ||
      (task.mode !== 'character-image' &&
        task.mode !== 'location-image' &&
        task.mode !== 'prop-image') ||
      task.resourceId !== resourceId ||
      !task.tagId ||
      !task.jobId ||
      !ACTIVE_RESTORED_TASK_STATUSES.has(task.status) ||
      jobs[task.tagId]
    ) {
      continue;
    }
    jobs[task.tagId] = { jobId: task.jobId };
  }
  return jobs;
}

export function normalizeCanvasGenerationStatus(
  status: AIJobStatusPayload['status'] | unknown
): CanvasGenerationStatus {
  if (status === 1 || status === 'pending') return 'pending';
  if (status === 2 || status === 'processing') return 'processing';
  if (status === 3 || status === 'completed') return 'completed';
  if (status === 4 || status === 5 || status === 'failed' || status === 'cancelled') {
    return 'failed';
  }
  return null;
}

/**
 * 分镜方案视频节点 loading：优先 job/再生成态；否则用 API video.status（裁剪 processing）。
 */
/** 当前用户本地裁剪中的视频：写入 generation map，避免误用共享 video.status。 */
export function buildOwnedTrimVideoGenerationByVideoId(
  videos: readonly {
    videoId: string;
    status?: number | null;
    statusStr?: string | null;
    progress?: number | null;
  }[],
  ownedTrimVideoIds: ReadonlySet<string>
): Record<string, CanvasGenerationNodeStatus> {
  if (ownedTrimVideoIds.size === 0) return {};
  const generationByVideoId: Record<string, CanvasGenerationNodeStatus> = {};
  for (const video of videos) {
    if (!ownedTrimVideoIds.has(video.videoId) || generationByVideoId[video.videoId]) continue;
    const isProcessing =
      video.status === 1 || video.statusStr === 'processing' || video.statusStr === 'pending';
    if (!isProcessing) continue;
    generationByVideoId[video.videoId] = {
      generationJobId: null,
      generationStatus: 'processing',
      generationProgress: typeof video.progress === 'number' ? video.progress : null,
      generationOverlayLabel: '视频裁剪中',
    };
  }
  return generationByVideoId;
}

export function resolveStoryboardPlanVideoGenerationOverlay(params: {
  videoStatus?: number | null;
  videoStatusStr?: string | null;
  videoProgress?: number | null;
  jobGeneration?: CanvasGenerationNodeStatus | null;
  /** 仅当前用户自己的裁剪/处理才允许用共享 video.status 兜底。 */
  allowApiProcessingOverlay?: boolean;
}): {
  generationJobId: string | null;
  generationStatus: CanvasGenerationStatus;
  generationProgress: number | null;
  generationEstimatedRemainingSeconds: number | null;
  generationOverlayLabel: string | null;
} {
  const job = params.jobGeneration ?? null;
  const jobStatus = job?.generationStatus ?? null;
  if (jobStatus === 'pending' || jobStatus === 'processing' || jobStatus === 'failed') {
    return {
      generationJobId: job?.generationJobId ?? null,
      generationStatus: jobStatus,
      generationProgress:
        typeof job?.generationProgress === 'number' ? job.generationProgress : null,
      generationEstimatedRemainingSeconds:
        typeof job?.generationEstimatedRemainingSeconds === 'number'
          ? job.generationEstimatedRemainingSeconds
          : null,
      generationOverlayLabel: job?.generationOverlayLabel ?? null,
    };
  }

  const isApiProcessing =
    params.allowApiProcessingOverlay === true &&
    (params.videoStatus === 1 ||
      params.videoStatusStr === 'processing' ||
      params.videoStatusStr === 'pending');
  if (isApiProcessing) {
    return {
      generationJobId: job?.generationJobId ?? null,
      generationStatus: 'processing',
      generationProgress:
        typeof params.videoProgress === 'number'
          ? params.videoProgress
          : typeof job?.generationProgress === 'number'
            ? job.generationProgress
            : null,
      generationEstimatedRemainingSeconds: null,
      generationOverlayLabel: '视频裁剪中',
    };
  }

  return {
    generationJobId: job?.generationJobId ?? null,
    generationStatus: jobStatus,
    generationProgress: typeof job?.generationProgress === 'number' ? job.generationProgress : null,
    generationEstimatedRemainingSeconds:
      typeof job?.generationEstimatedRemainingSeconds === 'number'
        ? job.generationEstimatedRemainingSeconds
        : null,
    generationOverlayLabel: null,
  };
}

export function getCanvasGenerationProgress(
  payload: AIJobStatusPayload | undefined
): number | null {
  if (!payload) return null;
  const progress =
    typeof payload.calculatedProgress === 'number' ? payload.calculatedProgress : payload.progress;
  return typeof progress === 'number' ? Math.max(0, Math.min(100, progress)) : null;
}

/** 画布 job payload → 失败 toast description。 */
export function getCanvasGenerationErrorMessage(
  payload: AIJobStatusPayload | undefined,
  fallback: string = AI_GENERATION_FAILURE_FALLBACK_MESSAGE
): string {
  return resolveAiGenerationFailureMessage(payload, fallback);
}

/** 与 preview-generation 的 readPositiveNumber 同语义：只接受有限正数，其余归 null。 */
export function getCanvasGenerationEstimatedRemainingSeconds(
  payload: AIJobStatusPayload | undefined
): number | null {
  const value = payload?.estimatedRemainingSeconds;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

export function resolveCanvasGenerationBatchStatusRefetchInterval(
  jobIds: string[],
  data: Record<string, AIJobStatusPayload> | undefined
): number | false {
  if (jobIds.length === 0) return false;
  if (!data) return 2000;

  const hasMissingJob = jobIds.some((jobId) => !data[jobId]);
  if (hasMissingJob) return 2000;

  const activeEntries = Object.values(data).filter(
    (status) => status.status === 1 || status.status === 2
  );
  if (activeEntries.length === 0) return false;

  return Math.min(
    ...activeEntries.map((status) => resolveGenerationPollIntervalMs(status.pollIntervalMs, 2000))
  );
}

function readLayoutMetaString(
  metaData: Record<string, unknown> | undefined,
  key: string
): string | null {
  const value = metaData?.[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function readLayoutMetaGenerationStatus(
  metaData: Record<string, unknown> | undefined
): CanvasGenerationStatus {
  const raw = readLayoutMetaString(metaData, 'generationStatus');
  if (raw === 'pending' || raw === 'processing' || raw === 'completed' || raw === 'failed') {
    return raw;
  }
  return null;
}

function isGenerationStartedByCurrentUser(
  metaData: Record<string, unknown> | undefined,
  currentUserId: string | null | undefined
): boolean {
  if (!currentUserId) return false;
  return readLayoutMetaString(metaData, 'generationStartedByUserId') === currentUserId;
}

function readLayoutMetaStringArray(
  metaData: Record<string, unknown> | undefined,
  key: string
): string[] {
  const value = metaData?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function readLayoutGenerationJobRecord(
  metaData: Record<string, unknown> | undefined
): CanvasGenerationJobRecord | null {
  const jobId = readLayoutMetaString(metaData, 'generationJobId');
  if (!jobId) return null;
  const jobIds = readLayoutMetaStringArray(metaData, 'generationJobIds');
  return {
    jobId,
    ...(jobIds.length > 0 ? { jobIds } : {}),
    conversationId: readLayoutMetaString(metaData, 'generationConversationId'),
    messageId: readLayoutMetaString(metaData, 'generationMessageId'),
    jobCreatedAt: readLayoutMetaString(metaData, 'generationJobCreatedAt'),
  };
}

function readLayoutGenerationNodeStatus(
  metaData: Record<string, unknown> | undefined,
  currentUserId?: string | null
): CanvasGenerationNodeStatus | null {
  const job = readLayoutGenerationJobRecord(metaData);
  if (!job) return null;
  const generationStatus = readLayoutMetaGenerationStatus(metaData);
  if (generationStatus === 'completed' || generationStatus === 'failed') return null;
  const rawProgress = metaData?.generationProgress;
  return {
    generationJobId: job.jobId,
    generationJobIds: job.jobIds && job.jobIds.length > 0 ? job.jobIds : null,
    generationStatus,
    generationProgress: typeof rawProgress === 'number' ? rawProgress : null,
    generationStartedByUserId: currentUserId ?? null,
    generationConversationId: job.conversationId,
    generationMessageId: job.messageId,
    generationJobCreatedAt: job.jobCreatedAt,
  };
}

/** 从已保存的画布 layout 恢复进行中的资产标签生成任务。 */
export function buildTagGenerationJobsFromSavedLayoutItems(
  savedItems: Array<{ itemType: string; itemId: string; metaData?: Record<string, unknown> }>,
  currentUserId?: string | null
): Record<string, CanvasGenerationJobRecord> {
  const jobs: Record<string, CanvasGenerationJobRecord> = {};

  for (const item of savedItems) {
    if (item.itemType !== 'asset_tag_image') continue;
    if (!isGenerationStartedByCurrentUser(item.metaData, currentUserId)) continue;
    const job = readLayoutGenerationJobRecord(item.metaData);
    if (!job) continue;

    const status = readLayoutMetaGenerationStatus(item.metaData);
    if (status === 'completed' || status === 'failed') continue;

    jobs[item.itemId] = job;
  }

  return jobs;
}

/** 从 layout 恢复标签生成节点的展示状态（刷新后、轮询返回前的兜底）。 */
export function buildTagGenerationStatusFromSavedLayoutItems(
  savedItems: Array<{ itemType: string; itemId: string; metaData?: Record<string, unknown> }>,
  currentUserId?: string | null
): Record<string, CanvasGenerationNodeStatus> {
  const statuses: Record<string, CanvasGenerationNodeStatus> = {};

  for (const item of savedItems) {
    if (item.itemType !== 'asset_tag_image') continue;
    if (!isGenerationStartedByCurrentUser(item.metaData, currentUserId)) continue;
    const status = readLayoutGenerationNodeStatus(item.metaData, currentUserId);
    if (!status) continue;
    statuses[item.itemId] = status;
  }

  return statuses;
}

/** 从已保存的画布 layout 恢复进行中的分镜图片生成任务。 */
export function buildStoryboardImageGenerationJobsFromSavedLayoutItems(
  savedItems: Array<{ itemType: string; itemId: string; metaData?: Record<string, unknown> }>,
  currentUserId?: string | null
): Record<string, CanvasGenerationJobRecord> {
  const jobs: Record<string, CanvasGenerationJobRecord> = {};

  for (const item of savedItems) {
    if (item.itemType !== 'storyboard_plan_image') continue;
    if (!isGenerationStartedByCurrentUser(item.metaData, currentUserId)) continue;
    const job = readLayoutGenerationJobRecord(item.metaData);
    if (!job) continue;

    const status = readLayoutMetaGenerationStatus(item.metaData);
    if (status === 'completed' || status === 'failed') continue;

    jobs[item.itemId] = job;
  }

  return jobs;
}

/**
 * 合并 layout 快照恢复的 job 与本地进行中的 job。
 * layout 回流不得整表覆盖，否则会抹掉尚未落库的 in-flight jobId。
 * 同一 plan 的 sibling jobIds 取并集，避免 fromTasks 单 id 覆盖 layout 全集。
 */
export function mergeCanvasGenerationJobsFromSavedLayout<T extends CanvasGenerationJobRecord>(
  current: Record<string, T>,
  fromLayout: Record<string, T>
): Record<string, T> {
  const keys = new Set([...Object.keys(current), ...Object.keys(fromLayout)]);
  const next: Record<string, T> = {};
  for (const key of keys) {
    const live = current[key];
    const saved = fromLayout[key];
    if (live && saved) {
      const jobIds = unionCanvasGenerationJobIds(saved, live);
      next[key] = {
        ...saved,
        ...live,
        jobIds: jobIds.length > 0 ? jobIds : (live.jobIds ?? saved.jobIds),
      };
    } else {
      next[key] = (live ?? saved) as T;
    }
  }
  return next;
}

/** 从 layout 恢复分镜图片生成节点的展示状态（刷新后、轮询返回前的兜底）。 */
export function buildStoryboardImageGenerationStatusFromSavedLayoutItems(
  savedItems: Array<{ itemType: string; itemId: string; metaData?: Record<string, unknown> }>,
  currentUserId?: string | null
): Record<string, CanvasGenerationNodeStatus> {
  const statuses: Record<string, CanvasGenerationNodeStatus> = {};

  for (const item of savedItems) {
    if (item.itemType !== 'storyboard_plan_image') continue;
    if (!isGenerationStartedByCurrentUser(item.metaData, currentUserId)) continue;
    const status = readLayoutGenerationNodeStatus(item.metaData, currentUserId);
    if (!status) continue;
    statuses[item.itemId] = status;
  }

  return statuses;
}
